import { IconAlertTriangle, IconCircleCheck, IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';
import { usePdfEditorStore } from '../state/usePdfEditorStore';
import { PanelCloseButton } from '../ui/prims';

/**
 * The accessibility results.
 *
 * Every row goes to the thing it is about, and the one issue that can be fixed
 * from a list — a picture with no alt text — is fixed here rather than sending
 * someone to the inspector for a field they would have to be told about.
 */
export default function AccessibilityPanel() {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const issues = usePdfEditorStore((s) => s.a11yIssues);
    const checked = usePdfEditorStore((s) => s.a11yChecked);
    const pages = usePdfEditorStore((s) => s.pages);
    const objectsByUid = usePdfEditorStore((s) => s.objectsByUid);
    const goToPage = usePdfEditorStore((s) => s.goToPage);
    const selectObject = usePdfEditorStore((s) => s.selectObject);
    const updateObject = usePdfEditorStore((s) => s.updateObject);
    const toggleAccessibility = usePdfEditorStore((s) => s.toggleAccessibility);

    const digits = (n) => (isBn ? toBnDigits(n) : String(n));
    const errors = issues.filter((issue) => issue.severity === 'error').length;

    return (
        <aside className="flex h-full w-[256px] flex-none flex-col border-l border-[#ECE8E4] bg-white dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex h-10 flex-none items-center justify-between px-3 text-[12px] font-bold text-[#3D3833] dark:text-zinc-200">
                {t('pdfEditor.a11y.title')}
                <div className="flex items-center gap-2">
                    {issues.length ? (
                        <span className={`text-[11px] font-semibold ${errors ? 'text-[#D33A2C]' : 'text-[#B7791F]'}`}>
                            {digits(issues.length)}
                        </span>
                    ) : null}
                    <PanelCloseButton onClick={toggleAccessibility} />
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 [scrollbar-width:thin]">
                {!checked ? (
                    <p className="px-2 py-6 text-center text-[11.5px] leading-relaxed text-[#A8A099] dark:text-zinc-500">
                        {t('pdfEditor.a11y.notRun')}
                    </p>
                ) : issues.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 px-2 py-8 text-center">
                        <IconCircleCheck size={26} stroke={1.7} className="text-[#1E9E5E]" />
                        <p className="text-[11.5px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                            {t('pdfEditor.a11y.clean')}
                        </p>
                    </div>
                ) : issues.map((issue) => {
                    const object = issue.objectId
                        ? (objectsByUid[pages[issue.pageIndex]?.uid] || []).find((o) => o.id === issue.objectId)
                        : null;
                    const Icon = issue.severity === 'error' ? IconAlertCircle : IconAlertTriangle;
                    return (
                        <div
                            key={issue.id}
                            className="mb-1.5 rounded-lg border border-[#ECE8E4] p-2 dark:border-zinc-700"
                        >
                            <button
                                type="button"
                                onClick={() => {
                                    goToPage(issue.pageIndex);
                                    if (issue.objectId) selectObject(issue.objectId);
                                }}
                                className="flex w-full items-start gap-2 text-left"
                            >
                                <Icon
                                    size={15}
                                    stroke={1.9}
                                    className={`mt-[1px] flex-none ${issue.severity === 'error' ? 'text-[#D33A2C]' : 'text-[#B7791F]'}`}
                                />
                                <span className="min-w-0 flex-1">
                                    <span className="block text-[11.5px] font-semibold leading-snug text-[#3D3833] dark:text-zinc-200">
                                        {t(`pdfEditor.a11y.kinds.${issue.kind}`)}
                                    </span>
                                    <span className="block text-[10.5px] text-[#A8A099]">
                                        {t('pdfEditor.a11y.onPage', { page: digits(issue.pageIndex + 1) })}
                                        {issue.detail?.ratio
                                            ? ` · ${issue.detail.ratio}:1 < ${issue.detail.needed}:1`
                                            : ''}
                                    </span>
                                </span>
                            </button>

                            {/* The one repair worth offering in a list. */}
                            {issue.kind === 'imageNoAlt' && object ? (
                                <input
                                    defaultValue={object.alt || ''}
                                    placeholder={t('pdfEditor.a11y.altPlaceholder')}
                                    onBlur={(e) => {
                                        const alt = e.target.value.trim();
                                        if (alt) updateObject(pages[issue.pageIndex].uid, object.id, { alt });
                                    }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                    className="mt-1.5 h-7 w-full rounded-md border border-[#ECE8E4] bg-white px-2 text-[11.5px] text-[#1C1815] outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                />
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </aside>
    );
}
